import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useShowApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from 'utils/network/axios'
import { PagingResponse } from 'entities'
import { Stocktaking, StocktakingForm, StocktakingSearchForm } from 'entities/stocktaking'

type StocktakingsResponse = PagingResponse & {
    results: Stocktaking[]
}

export function useFetchStocktakingsApi(searchForm?: Form<StocktakingSearchForm>): IndexApiSet<StocktakingsResponse> & { execute: () => void } {
    const apiPath = 'stocktakings/'
    const api = useIndexApi<StocktakingsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

type StocktakingResponse = BaseResponse & Stocktaking
export function useFetchStocktakingApi(): ApiSet<StocktakingResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<StocktakingResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `stocktakings/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostStocktakingApi(): ApiSet<BaseResponse> & { execute: (form: Form<StocktakingForm>) => void } {
    const apiSet = usePostApi<BaseResponse, StocktakingForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<StocktakingForm>) => {
        const apiPath = `stocktakings/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutStocktakingApi(): ApiSet<BaseResponse> & { execute: (form: StocktakingForm) => void } {
    const apiSet = usePutApi<BaseResponse, StocktakingForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: StocktakingForm) => {
        const apiPath = `stocktakings/${form.id}/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutStocktakingsApi(): ApiSet<BaseResponse> & { execute: (form: Form<StocktakingForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, StocktakingForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<StocktakingForm[]>) => {
        const apiPath = `stocktakings/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteStocktakingApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `stocktakings/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
